import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from "@angular/core";
import { UtilsService } from "@core/services/utils/utils.service";
import { TitleGenerationService } from "@features/forum/services/title-generation.service";
import { FieldType } from "@ngx-formly/core";
import { Subject, BehaviorSubject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
  selector: "astrobin-formly-field-title-with-ai",
  templateUrl: "./formly-field-title-with-ai.component.html",
  styleUrls: ["./formly-field-title-with-ai.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FormlyFieldTitleWithAiComponent extends FieldType implements OnInit, OnDestroy {
  isTitleAiGenerated = false;
  isTitleGenerating$: BehaviorSubject<boolean>;
  canGenerateTitle = false;
  hasBodyChangedSinceLastGeneration = false;
  isAutoGenerationEnabled = true; // Always enabled

  // For tracking whether to display the field in DOM
  hasContent = false;

  // Suggestion state
  hasPendingSuggestion = false;
  pendingSuggestion = "";

  private readonly destroy$ = new Subject<void>();
  private userEnteredTitle = "";
  private titleManuallyEdited = false;
  private lastProcessedBodyContent = "";

  constructor(
    private readonly titleGenerationService: TitleGenerationService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly utilsService: UtilsService
  ) {
    super();
    this.isTitleGenerating$ = this.titleGenerationService.isGenerating$;
  }

  // Helper method to safely check if generation is in progress
  private isGenerating(): boolean {
    return this.titleGenerationService.isGenerating$.getValue();
  }

  ngOnInit(): void {
    // Setup change detection on the title generation state
    this.isTitleGenerating$.pipe(takeUntil(this.destroy$)).subscribe(() => this.changeDetectorRef.markForCheck());

    // Listen for body content updates from parent through field definition
    if (this.props.bodyContent$) {
      this.props.bodyContent$.pipe(takeUntil(this.destroy$)).subscribe(content => {
        // Check if content meets minimum requirements
        this.canGenerateTitle = !!content && content.length >= TitleGenerationService.MIN_CONTENT_LENGTH;

        // Track content changes and pass auto-generation flag to service
        // This ensures the service knows whether to process the content or not
        this.titleGenerationService.processContentChange(content, this.isAutoGenerationEnabled);

        // Update change tracking only if content has changed
        const contentHasChanged = this._checkForContentChanges(content);

        this.changeDetectorRef.markForCheck();
      });
    }

    // Also listen for changes in the parent form if available
    if (this.props.form) {
      this.props.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
        // The form value contains JSON, but we need plain text for generation
        // The bodyContent$ observable already handles this, so we don't need to process form changes here
        // This was causing issues because it was passing JSON to the title generation service
      });
    }

    // Subscribe to automatically generated titles
    this.titleGenerationService.autoGeneratedTitle$.pipe(takeUntil(this.destroy$)).subscribe(title => {
      // Show as suggestion if:
      // 1. Field is empty, OR
      // 2. It's already an AI title that hasn't been manually edited
      // This ensures we respect user edits unless they click refresh
      if (!this.formControl.value || (this.isTitleAiGenerated && !this.titleManuallyEdited)) {
        // Show as suggestion instead of directly setting value
        this.pendingSuggestion = title;
        this.hasPendingSuggestion = true;

        // Get the current body content
        let currentBodyContent = "";
        if (this.props.form && this.props.form.value && this.props.form.value.body) {
          currentBodyContent = this.props.form.value.body;
        } else if (typeof this.props.bodyContent === "function") {
          currentBodyContent = this.props.bodyContent();
        } else if (this.props.bodyContent) {
          currentBodyContent = this.props.bodyContent;
        }

        // Store the processed content to track future changes
        if (currentBodyContent) {
          this.lastProcessedBodyContent = currentBodyContent;
          this.hasBodyChangedSinceLastGeneration = false;
        }

        this.changeDetectorRef.detectChanges();
      }
    });

    // Initialize field state if there's a value
    if (this.formControl.value) {
      this.hasContent = true;

      // Set AI generated flag if provided through field props
      if (this.props.isAiGenerated !== undefined) {
        this.isTitleAiGenerated = this.props.isAiGenerated;
      }

      // Set manually edited flag if provided
      if (this.props.isManuallyEdited !== undefined) {
        this.titleManuallyEdited = this.props.isManuallyEdited;
      }
    }

    // Check initial body content when component is first initialized
    this.utilsService.delay(0).subscribe(() => {
      // Get initial body content
      let initialBodyContent = "";
      if (this.props.form && this.props.form.value && this.props.form.value.body) {
        initialBodyContent = this.props.form.value.body;
      } else if (typeof this.props.bodyContent === "function") {
        initialBodyContent = this.props.bodyContent();
      } else if (this.props.bodyContent) {
        initialBodyContent = this.props.bodyContent;
      }

      // Update canGenerateTitle based on initial content
      if (initialBodyContent && initialBodyContent.length >= TitleGenerationService.MIN_CONTENT_LENGTH) {
        this.canGenerateTitle = true;
        this.lastProcessedBodyContent = initialBodyContent;
        this.changeDetectorRef.markForCheck();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Triggered when user manually edits the title
   */
  onInput(): void {
    this.hasContent = !!this.formControl.value;

    // If we had an AI-generated title and user is editing it,
    // update the state to prevent auto-updates to the title
    if (this.isTitleAiGenerated) {
      this.isTitleAiGenerated = false;
      this.titleManuallyEdited = true;
      this.changeDetectorRef.markForCheck();
    }

    // Call parent handler if provided
    if (this.props.onTitleChange) {
      this.props.onTitleChange(this.formControl.value, this.isTitleAiGenerated, this.titleManuallyEdited);
    }
  }

  /**
   * Check if body content has changed since the last generation
   * @returns true if content has changed, false otherwise
   */
  private _checkForContentChanges(content: string): boolean {
    const contentChanged = content !== this.lastProcessedBodyContent;

    // If content is different, mark as changed
    if (contentChanged) {
      this.hasBodyChangedSinceLastGeneration = true;
      this.changeDetectorRef.markForCheck();
      return true;
    }

    return false;
  }

  /**
   * Generate title using the AI service
   * @param isAutoGenerated true if this is triggered by auto-generation, false if user clicked the button
   */
  generateTitle(isAutoGenerated: boolean = false): void {
    // If this is an auto-generated request and auto-generation is disabled, do nothing
    if (isAutoGenerated && !this.isAutoGenerationEnabled) {
      return;
    }
    // Get the current body content using the getter if available,
    // or directly from props if not
    let bodyContent = "";

    if (typeof this.props.bodyContent === "function") {
      // If bodyContent is a getter function
      bodyContent = this.props.bodyContent();
    } else if (this.props.form && this.props.form.value && this.props.form.value.body) {
      // If we have a reference to the parent form
      bodyContent = this.props.form.value.body;
    } else if (this.props.bodyContent) {
      // Fallback to static value
      bodyContent = this.props.bodyContent;
    }

    // Don't proceed if generation is already in progress
    if (this.isGenerating()) {
      return;
    }

    // Save the current body content to track changes
    this.lastProcessedBodyContent = bodyContent;
    this.hasBodyChangedSinceLastGeneration = false;

    // If content is less than minimum required length, clear the title
    if (!bodyContent || bodyContent.length < TitleGenerationService.MIN_CONTENT_LENGTH) {
      this.formControl.setValue("", { onlySelf: true, emitEvent: false });
      this.hasContent = false;
      this.isTitleAiGenerated = false;
      this.titleManuallyEdited = false;

      // Call parent handler if provided
      if (this.props.onTitleChange) {
        this.props.onTitleChange("", false, false);
      }

      this.changeDetectorRef.detectChanges();
      return;
    }

    // Remember user entered title if we had one
    if (this.formControl.value && !this.isTitleAiGenerated) {
      this.userEnteredTitle = this.formControl.value;
    }

    // Request title generation
    this.titleGenerationService.generateTitleManually(bodyContent).subscribe({
      next: title => {
        if (title) {
          // Show as suggestion instead of directly setting value
          this.pendingSuggestion = title;
          this.hasPendingSuggestion = true;

          // Force change detection
          this.changeDetectorRef.detectChanges();
        }
      },
      error: error => {
        console.error("Error during title generation:", error);
      }
    });
  }

  /**
   * Handle generate title button click
   */
  onGenerateTitleClick(tooltip: any): void {
    if (this.formControl.disabled || !this.canGenerateTitle) {
      return;
    }
    this.generateTitle();
    tooltip.close();
  }

  /**
   * Accept the AI-generated suggestion
   */
  acceptSuggestion(): void {
    if (this.pendingSuggestion) {
      this.formControl.setValue(this.pendingSuggestion, { emitEvent: true });
      this.hasContent = true;
      this.isTitleAiGenerated = true;
      this.titleManuallyEdited = false;

      // Call parent handler if provided
      if (this.props.onTitleChange) {
        this.props.onTitleChange(this.pendingSuggestion, true, false);
      }

      // Clear suggestion state
      this.pendingSuggestion = "";
      this.hasPendingSuggestion = false;

      this.changeDetectorRef.detectChanges();
    }
  }

  /**
   * Reject the AI-generated suggestion
   */
  rejectSuggestion(): void {
    // Clear suggestion state
    this.pendingSuggestion = "";
    this.hasPendingSuggestion = false;

    this.changeDetectorRef.detectChanges();
  }
}
